// CustomSelect.tsx
import classNames from 'classnames';
import React from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FormattedSelectOptions } from '../../utils/types/analytics/analytics_types';
import { SelectOptions } from '../../utils/types/react_select_types';
import styles from './custom_select.module.css';

const customStyles = {
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e2e8f0',
  }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '36px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: state.isDisabled ? '#f8fafc' : '#ffffff',
    borderColor: state.isFocused ? '#10b981' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(16, 185, 129, 0.1)' : 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: state.isFocused ? '#10b981' : '#cbd5e1',
    },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#94a3b8',
    fontSize: '13px',
    marginLeft: '0.25rem',
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: '0 0.25rem',
    color: '#475569',
    fontSize: '13px',
  }),
  singleValue: (base: any) => ({
    ...base,
    margin: '0 0.25rem',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '500',
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '2px 8px',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: '0 0.5rem',
    color: '#94a3b8',
    cursor: 'pointer',
    '&:hover': {
      color: '#64748b',
    },
    svg: {
      width: '16px',
      height: '16px',
    },
  }),
  clearIndicator: (base: any) => ({
    ...base,
    padding: '0 0.75rem',
    svg: {
      width: '16px',
      height: '16px',
    },
  }),
  option: (base: any, state: any) => {
    let backgroundColor = 'transparent';
    if (state.isSelected) backgroundColor = '#f0fdf4';
    else if (state.isFocused) backgroundColor = '#f8fafc';

    return {
      ...base,
      backgroundColor,
      color: state.isSelected ? '#059669' : '#475569',
      cursor: 'pointer',
      fontSize: '13px',
      padding: '8px 12px',
      textAlign: 'left',
      ':active': {
        backgroundColor: '#f0fdf4',
      },
    };
  },
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: '#ecfdf5',
    borderRadius: '9999px',
    fontSize: '11px',
    margin: '2px',
    border: '1px solid #d1fae5',
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: '#059669',
    padding: '1px 8px',
    fontWeight: '600',
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: '#10b981',
    borderRadius: '9999px',
    ':hover': {
      backgroundColor: '#10b981',
      color: '#ffffff',
    },
  }),
};

type CustomSelectProps = {
  placeholder?: string;
  label?: string;
  name: string;
  options: SelectOptions[] | FormattedSelectOptions[] | undefined;
  value?: SelectOptions | SelectOptions[] | null;
  onChange: any;
  onInputChange?: any;
  disabled?: boolean;
  creatable?: boolean;
  clearable?: boolean;
  isMulti?: boolean;
  components?: any;
  isLoading?: boolean;
  onCreateOption?: ((inputValue: string) => void);
};

export const CustomSelect = ({
  label,
  name,
  options,
  value,
  onChange,
  onInputChange,
  disabled = false,
  creatable = false,
  clearable = false,
  isMulti,
  components,
  isLoading,
  onCreateOption,
  placeholder = 'Search',
}: CustomSelectProps) => {
  return (
    <div className='w-full'>
      { label && (
        <label
            htmlFor={name}
            className='block mb-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-0.5'
        >
          { label }
        </label>
      ) }

      { creatable ? (
        <CreatableSelect
            className={classNames(styles.wrapper)}
            classNamePrefix='custom-select'
            value={value}
            name={name}
            options={options}
            onChange={onChange}
            onInputChange={onInputChange}
            styles={customStyles}
            isDisabled={disabled}
            isLoading={isLoading}
            // @ts-ignore
            isMulti={isMulti}
            onCreateOption={onCreateOption}
            isClearable={clearable}
            menuPortalTarget={
              typeof window !== 'undefined' ? document.body : null
            }
        />
      ) : (
        <Select
            className={classNames(styles.wrapper)} // optional extra wrapper style
            classNamePrefix='custom-select'
            placeholder={placeholder}
            value={value}
            name={name}
            options={options}
            onChange={onChange}
            onInputChange={onInputChange}
            styles={customStyles}
            isDisabled={disabled}
            isLoading={isLoading}
            // @ts-ignore
            isMulti={isMulti}
            isClearable={clearable}
            maxMenuHeight={200}
            components={components}
            menuPortalTarget={
              typeof window !== 'undefined' ? document.body : null
            }
        />
      ) }
    </div>
  );
};
